import React from "react";
import FlexibleTable from "../../../modules/FlexibleTable";
import moment from "moment";
import { formatToMB, formatBitsPerSecond } from "../../../utils/StringUtil";

interface DiagnosticsMetricsAdvancedTableProps {
  dynamic_metric: string;
  metric_data: MetricDataSet[];
  selected_device_ids: Item[];
  loaded: boolean;
}

interface Item {
  value: string;
  text?: string | undefined;
}

interface MetricSample {
  time: number;
  [key: string]: number | string;
}

interface MetricDataSet {
  deviceId: string;
  data: MetricSample[];
}

const DiagnosticsMetricsAdvancedTable: React.FC<DiagnosticsMetricsAdvancedTableProps> = ({
  dynamic_metric = "",
  metric_data = [],
  selected_device_ids = [],
  loaded = false,
}) => {
  const dataFormatter = React.useCallback(
    (data: string | number) => {
      if (dynamic_metric === "firewall.transfer.persec") {
        return formatBitsPerSecond(typeof data === "string" ? Number(data) : data);
      } else {
        return formatToMB(typeof data === "number" ? String(data) : data);
      }
    },
    [dynamic_metric]
  );

  const columns = React.useMemo(() => {
    const cols = [
      {
        title: "Time",
        data: function (row: MetricSample) {
          const day = moment.unix(row["time"]);
          return day.format("YYYY-MM-DD HH:mm:ss");
        },
        export: function (row: MetricSample) {
          const day = moment.unix(row["time"]);
          return day.format("YYYY-MM-DD HH:mm:ss");
        },
        search: FlexibleTable.search__single_string_field("time"),
        onclick: undefined,
      },
    ];
    if (selected_device_ids.length === 1) {
      cols.push({
        title: "Value",
        data: function (row: MetricSample) {
          return String(row["value"]);
        },
        export: function (row: MetricSample) {
          return String(row["value"]);
        },
        onclick: undefined,
        search: FlexibleTable.search__single_string_field("value"),
      });
    } else {
      selected_device_ids.forEach((item) => {
        const dev = item["value"];
        const col = dev.split(".")[0];
        cols.push({
          title: col,
          data: function (row: MetricSample) {
            return row[col] === null ? "" : String(row[col]);
          },
          export: function (row: MetricSample) {
            return row[col] === null ? "" : String(row[col]);
          },
          onclick: undefined,
          search: FlexibleTable.search__single_string_field(col),
        });
      });
    }
    return cols;
  }, [selected_device_ids]);

  type Row = { time: number } & Record<string, number | undefined>;

  const data = React.useMemo(() => {
    if (metric_data.length === 0) return [];
    if (metric_data.length === 1) {
      const metricData: MetricSample[] = [];
      metric_data[0].data.forEach((item) => {
        metricData.push({
          time: item.time,
          value: dataFormatter(item.value),
        });
      });
      return metricData;
    }
    // convert to single object with device ids as keys
    return metric_data[0].data.map(
      (s0, i) =>
        Object.fromEntries([
          ["time", s0.time],
          ...metric_data.map((ds) => [ds.deviceId.split(".")[0], dataFormatter(ds.data[i]?.value)]),
        ] as const) as Row
    );
  }, [metric_data, dataFormatter]);

  const columnsKey = React.useMemo(() => columns.map((c) => c.title).join("."), [columns]);

  return (
    <div className="main-content-sub-element">
      <FlexibleTable
        key={columnsKey}
        columns={columns}
        data={data}
        sort_column={0}
        sort_asc={true}
        loaded={loaded}
        className={"performance-metrics"}
      />
    </div>
  );
};

export default DiagnosticsMetricsAdvancedTable;
